import React from "react";
import ProductCardQuickAdd from "../../ProductCard.QuickAdd";
import FavoriteButton from '../../FavoriteButton';
import { translate } from "../../../Services/translation";
import { HitCurrencyPrices, HitPrices } from "./types/HitPrices";
import { HitProps } from "./types/HitProps";
import { getFilterBackgroud } from "../../../Services/FacetedSearch.service";
import HitSizePicker from "./HitSizePicker";
import { HitImages, HitUrl } from "../utils/HitUtils";
import InjectedContent from "./InjectedContent";

const fallbackPrice = {
  formattedCampaignPrice: "",
  formattedPrice: "",
  formattedDiscountInPercentage: "",
  actualPrice: 0,
  discount: 0,
};

const GetPriceGroup = (price: HitPrices) => {
  if (window.__litium.isLoggedIn) {
    return {
      formattedCampaignPrice: price.loggedIn.formattedCampaignPrice,
      formattedPrice: price.loggedIn.formattedPrice,
      formattedDiscountInPercentage:
        price.loggedIn.formattedDiscountInPercentage,
      actualPrice: price.loggedIn.actualPrice,
      discount: price.loggedIn.discount,
    };
  }

  return {
    formattedCampaignPrice: price.anonymous.formattedCampaignPrice,
    formattedPrice: price.anonymous.formattedPrice,
    formattedDiscountInPercentage:
      price.anonymous.formattedDiscountInPercentage,
    actualPrice: price.anonymous.actualPrice,
    discount: price.anonymous.discount,
  };
};

export const GetPricesOfCurrency = (price: HitCurrencyPrices) => {
  const userCurrency = window.__litium.preloadState.cart.currency;

  if (userCurrency in price) {
    return GetPriceGroup(price[userCurrency]);
  }

  return fallbackPrice;
};

const UnsafeHit = ({ hit }: HitProps) => {
  if (hit.__injectedContent) {
    return <InjectedContent content={hit.__injectedContent} />;
  }

  const priceGroup = GetPricesOfCurrency(hit.price);
  const formattedCampaignPrice = priceGroup.formattedCampaignPrice;
  const formattedPrice = priceGroup.formattedPrice;
  const formattedDiscountInPercentage =
    priceGroup.formattedDiscountInPercentage;
  const shouldShowQuickAdd = hit.variants.length > 0;
  const isInStock = hit.variants.some((v) => v.inStock);
  const anyOutOfStock = hit.variants.some((v) => !v.inStock);
  const unitOfMeasurementTitle = "st";
  const sizeModels = hit.variants.map((variant, i) => {
    return {
      size: variant.size,
      isInStock: variant.inStock,
      articleNumber: variant.id,
    };
  });

  const imageUrls = HitImages(hit);
  const url = HitUrl(hit);
  const styleName = hit.styleName || hit.name;

  return (
      <div
        className={`product__wrapper ${!isInStock && "not-in-stock"}`}
        data-insights-object-id={hit.objectID}
        data-insights-position={hit.__position}
        data-insights-query-id={hit.__queryID}
      >
        <figure className="product__figure">
          <a href={url} className="product__image-link">
            {imageUrls.length > 0 ? (
              imageUrls.map((img, i) => (
                <img
                  key={img + i}
                  src={img}
                  alt={hit.name}
                  className={
                  i == 0 ? "product__image--first" : "product__image--secondary"
                  }
                  loading="lazy"
                />
              ))
            ) : (
              <span>{translate("product.missing-image")}</span>
            )}
          </a>
          <div className="product__favorite-button">
            <FavoriteButton
              item={hit.objectID}
              productData={{
                productName: styleName,
                brand: hit.brand,
                price: priceGroup.actualPrice?.toString(),
                imageUrl: hit.imageUrl,
                url: url,
              }}
            />
          </div>
          {shouldShowQuickAdd && (
            <div className="product__quick-add-container">
              <ProductCardQuickAdd
                sizeModels={sizeModels}
                sizeHintShort={hit.sizeHintShort}
                sizeHint=""
                sizeHintValue={0}
                shouldShowMiniCartOnAdd={true}
                algoliaQueryId={hit.__queryID}
              >
                {anyOutOfStock && (
                  <div className="product__quick-add-container__out-of-stock">
                    {translate("product.out-of-stock-text")}
                    <HitSizePicker
                      className="product__quick-add-container__out-of-stock__link"
                      hit={hit}
                      formattedPrice={formattedPrice}
                      formattedCampaignPrice={formattedCampaignPrice}
                      formattedDiscountInPercentage={
                        formattedDiscountInPercentage
                      }
                    >
                      {translate("product.out-of-stock-link")}
                    </HitSizePicker>
                  </div>
                )}
              </ProductCardQuickAdd>
            </div>
          )}
        </figure>
        <article className="product__info">
          <meta content={hit.description} />
          {formattedPrice && <meta content={unitOfMeasurementTitle} />}
          <div className="product__info--header">
            <a href={url} title={styleName}>
              <div>
              {hit.brand && <span className="product__brand">{hit.brand}</span>}
              </div>
              {styleName && <h3 className="product__name">{styleName}</h3>}
            </a>
            <HitSizePicker
              className="product__info__mobile-add-to-cart"
              hit={hit}
              formattedPrice={formattedPrice}
              formattedCampaignPrice={formattedCampaignPrice}
              formattedDiscountInPercentage={formattedDiscountInPercentage}
            >
              <i className="icon-bag"></i>
            </HitSizePicker>
          </div>
          <div className="product__algolia-price">
            {formattedCampaignPrice && formattedPrice ? (
              <>
                <span className="product__algolia-price__campaign-price">
                  {formattedCampaignPrice}
                </span>
                <span className="product__algolia-price__old-price">
                  {formattedPrice}
                </span>
                <span className="product__algolia-price__amount-off">
                  {formattedDiscountInPercentage}
                </span>
              </>
            ) : formattedPrice ? (
              <span>{formattedPrice}</span>
            ) : null}
            {isInStock ? (
              <link href="http://schema.org/InStock" />
            ) : (
              <link href="http://schema.org/OutOfStock" />
            )}
            <link href={hit.url} />
          </div>
          {hit.voucherCode && hit.voucherCodeDiscountPercent && (
            <div className="product__usps">
              <span className="product__usps--discount">
                {translate("product.voucher-code").replace(
                  "{0}",
                  hit.voucherCodeDiscountPercent
                )}
                &nbsp;
                <span className="product__usps--discount--amount">
                  {hit.voucherCode}
                </span>
              </span>
            </div>
          )}
          <div className="product__tag-color-wrapper">
            {hit.tags.length > 0 && (
              <div className="product__tags product__tags--algolia">
                {hit.tags.map((tag) => (
                  <span
                    key={tag.name}
                    style={{
                      backgroundColor: tag.backgroundColor,
                      color: tag.textColor,
                      borderColor: tag.borderColor,
                    }}
                    className="product__tags--single-tag"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
            {hit.colorRelatedProducts.length > 0 && (
              <div className="product__color-options">
                {hit.colorRelatedProducts.map((colorRelatedProduct) => {
                  const hexCode = colorRelatedProduct.hexCode;
                  const background = hexCode ? getFilterBackgroud(hexCode) : "";
                  const border =
                    hexCode.toUpperCase() === "#FFFFFF"
                      ? "1px solid #E0E0E0"
                      : "";

                  return (
                    <a
                    key={colorRelatedProduct.hexCode + colorRelatedProduct.url}
                      href={colorRelatedProduct.url}
                      style={{
                        background,
                        border,
                      }}
                    ></a>
                  );
                })}
              </div>
            )}
          </div>
          {!isInStock && (
            <div className="product__info__out-of-stock">
              <HitSizePicker
                className="product__info__out-of-stock__link"
                hit={hit}
                formattedPrice={formattedPrice}
                formattedCampaignPrice={formattedCampaignPrice}
                formattedDiscountInPercentage={formattedDiscountInPercentage}
              >
                {translate("product.out-of-stock-link")}
              </HitSizePicker>
            </div>
          )}
        </article>
      </div>
  );
};

const Hit = ({ hit }: HitProps) => {
  try {
    return UnsafeHit({ hit });
  } catch (error) {
    console.warn(error);
    return <></>;
  }
};

export default Hit;
